// CMSCustomHours.jsx
import React, { useRef, useState } from "react";
import NoDataFound from "../../Extra/NoDataFound";
import DownloadExcel from "../../Extra/DownloadExcel";
import LinearRangeSlider from "../../CHARTS/LinearRangeSlider";
// --------------------------------------------------------
// MAIN COMPONENT
// --------------------------------------------------------
const CMSCustomHours = ({ data = [], headObject, columnLabels, otherKey }) => {
  const tableRef = useRef();
  //////////////////////////////////////////////////////////////////////////////////////

  const hourColumns = Object.values(headObject); // DB column names
  const hourLabels = Object.keys(headObject); // Display labels
  const [rangeState, setRangeState] = useState({
    minCol: hourColumns[0],
    maxCol: hourColumns[hourColumns.length - 1],
    minLabel: hourLabels[0],
    maxLabel: hourLabels[hourLabels.length - 1],
    isFullyMerged: false,
  });

  // Labels for table header

  // --------------------------------------------------------
  // Compute Monthly Totals
  // --------------------------------------------------------
  function getMonthlyTotals(data) {
    if (!data || data.length === 0) return [];
    const map = {};
    data.forEach((obj) => {
      const key = `${obj.cms_year}-${obj.cms_month}`;
      if (!map[key]) {
        map[key] = {
          cms_year: obj.cms_year,
          cms_month: obj.cms_month,
          wh_lobby: "TOTAL",
          wh_total: 0,
          ...Object.fromEntries(hourColumns.map((c) => [c, 0])),
        };
      }
      hourColumns.forEach((col) => {
        map[key][col] += Number(obj[col] || 0);
      });
      map[key].wh_total += Number(obj.wh_total || 0);
    });
    return Object.values(map);
  }

  // --------------------------------------------------------
  // Merge helpers
  // --------------------------------------------------------
  function mergeLeft(obj, minCol) {
    const idx = hourColumns.indexOf(minCol);
    if (idx <= 0) return 0;
    return hourColumns
      .slice(0, idx)
      .reduce((sum, col) => sum + Number(obj[col] || 0), 0);
  }

  function mergeRight(obj, maxCol) {
    const idx = hourColumns.indexOf(maxCol);
    if (idx >= hourColumns.length - 1) return 0;
    return hourColumns
      .slice(idx + 1)
      .reduce((sum, col) => sum + Number(obj[col] || 0), 0);
  }

  // Merge everything up to a column (used when fully merged)
  function mergeUpTo(obj, upToCol) {
    const idx = hourColumns.indexOf(upToCol);
    return hourColumns
      .slice(0, idx + 1)
      .reduce((sum, col) => sum + Number(obj[col] || 0), 0);
  }

  // ////////////////////////////////////////////////////////////////////////////////

  const rows = data?.filter((o) => o.wh_lobby !== "TOTAL");
  const totals = getMonthlyTotals(rows);

  // Determine visible individual columns
  const visibleCols = rangeState.isFullyMerged
    ? []
    : hourColumns.slice(
        hourColumns.indexOf(rangeState.minCol),
        hourColumns.indexOf(rangeState.maxCol) + 1
      );

  const hasLeftMerge =
    !rangeState.isFullyMerged && hourColumns.indexOf(rangeState.minCol) > 0;
  const hasRightMerge =
    !rangeState.isFullyMerged &&
    hourColumns.indexOf(rangeState.maxCol) < hourColumns.length - 1;

  return (
    <div className="">
      <div className="bg-dark text-white ps-2 p-1 d-flex align-items-center justify-content-between">
        <h5>Crew Working Hours (Custom Range Merge)</h5>
      </div>

      {/* Dual Slider with Overlapping Support */}
      <LinearRangeSlider
        range={headObject}
        onRangeChange={(newRange) => {
          setRangeState({
            minCol: newRange.minCol,
            maxCol: newRange.maxCol,
            minLabel: newRange.minLabel,
            maxLabel: newRange.maxLabel,
            isFullyMerged: newRange.isFullyMerged,
          });
        }}
      />
      <div className="newUiBody">
        <div className="table-responsive ">
          <div style={{ maxHeight: "400px", overflow: "auto" }}>
            <table
              className="table newUiTable table-striped table-hover"
              ref={tableRef}
              style={{ fontSize: "12px" }}
            >
              <thead className="sticky-top table-dark">
                <tr>
                  <th>SNo</th>
                  {otherKey &&
                    otherKey.map((keyName, k) => {
                      return <th key={k}>{keyName.replace("wh_", "")}</th>;
                    })}

                  {/* Left merged column */}
                  {hasLeftMerge && <th>≤ {rangeState.minLabel} hrs</th>}

                  {/* Fully merged single column */}
                  {rangeState.isFullyMerged && (
                    <th>All ≤ {rangeState.minLabel} hrs</th>
                  )}

                  {/* Visible individual columns */}
                  {visibleCols.map((col) => (
                    <th key={col}>{columnLabels[col]}</th>
                  ))}

                  {/* Right merged column */}
                  {hasRightMerge && <th>≥ {rangeState.maxLabel} hrs</th>}

                  <th>Total</th>
                </tr>
              </thead>

              <tbody>
                {/* Regular Rows */}
                {rows?.map((obj, ind) => (
                  <tr key={ind}>
                    <td>{ind + 1}</td>

                    {otherKey &&
                      otherKey.map((keyName, k) => {
                        return <td key={k}>{obj[keyName]}</td>;
                      })}
                    {/* <td>{obj.cms_year}</td>
                  <td>{obj.cms_month}</td>
                  <td>{obj.wh_lobby}</td> */}

                    {/* Left merged */}
                    {hasLeftMerge && (
                      <td>{mergeLeft(obj, rangeState.minCol)}</td>
                    )}

                    {/* Fully merged single column */}
                    {rangeState.isFullyMerged && (
                      <td>{mergeUpTo(obj, rangeState.minCol)}</td>
                    )}

                    {/* Visible individual columns */}
                    {visibleCols.map((col) => (
                      <td key={col}>{obj[col] || 0}</td>
                    ))}

                    {/* Right merged */}
                    {hasRightMerge && (
                      <td>{mergeRight(obj, rangeState.maxCol)}</td>
                    )}

                    <td>{obj.wh_total || 0}</td>
                  </tr>
                ))}

                {/* Totals Row */}
                {/* {totals?.map((tot, i) => (
                <tr key={`total-${i}`} className="table-dark fw-bold">
                <td colSpan={3}></td>
                <td>TOTAL</td>
                
                {hasLeftMerge && <td>{mergeLeft(tot, rangeState.minCol)}</td>}
                
                {rangeState.isFullyMerged && (
                  <td>{mergeUpTo(tot, rangeState.minCol)}</td>
                  )}
                  
                  {visibleCols.map((col) => (
                    <td key={col}>{tot[col] || 0}</td>
                    ))}
                    
                    {hasRightMerge && (
                      <td>{mergeRight(tot, rangeState.maxCol)}</td>
                      )}
                      
                      <td>{tot.wh_total || 0}</td>
                      </tr>
                      ))} */}
              </tbody>
            </table>
          </div>
        </div>

        {rows?.length === 0 ? (
          <NoDataFound />
        ) : (
          <DownloadExcel tableRef={tableRef} fileName="Crew Working Hours" />
        )}
      </div>
    </div>
  );
};

export default CMSCustomHours;
